import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  FiChevronDown,
  FiChevronUp,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiSliders,
  FiX,
} from 'react-icons/fi';

const toDateValue = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getPresetDates = (preset, currentDate = new Date()) => {
  const end = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
  const start = new Date(end);

  if (preset === 'today') return { date_from: toDateValue(start), date_to: toDateValue(end) };
  if (preset === 'yesterday') {
    start.setDate(start.getDate() - 1);
    return { date_from: toDateValue(start), date_to: toDateValue(start) };
  }
  if (preset === 'this-month') {
    start.setDate(1);
    return { date_from: toDateValue(start), date_to: toDateValue(end) };
  }
  if (preset === 'last-month') {
    start.setMonth(start.getMonth() - 1, 1);
    end.setDate(0);
    return { date_from: toDateValue(start), date_to: toDateValue(end) };
  }

  start.setDate(start.getDate() - (preset === '7-days' ? 6 : 29));
  return { date_from: toDateValue(start), date_to: toDateValue(end) };
};

export const getSalesReportQuery = (search = '') => {
  const params = new URLSearchParams(search);
  const defaultDates = getPresetDates('30-days');
  return {
    date_from: params.get('date_from') || defaultDates.date_from,
    date_to: params.get('date_to') || defaultDates.date_to,
    keyword: params.get('keyword') || '',
    salesperson: params.get('salesperson') || '',
    created_user: params.get('created_user') || '',
    page: Number(params.get('page')) || 1,
    items_per_page: Number(params.get('items_per_page')) || 20,
  };
};

const PRESETS = [
  ['today', 'Hôm nay'],
  ['yesterday', 'Hôm qua'],
  ['7-days', '7 ngày gần nhất'],
  ['30-days', '30 ngày gần nhất'],
  ['this-month', 'Tháng này'],
  ['last-month', 'Tháng trước'],
];

const formatShortDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const findActivePreset = (values) => {
  const match = PRESETS.find(([preset]) => {
    const range = getPresetDates(preset);
    return range.date_from === values.date_from && range.date_to === values.date_to;
  });
  return match?.[0] || 'custom';
};

const SalesReportFilters = ({
  includeDetailFilters = false,
  includeKeywordFilter = false,
  keywordLabel = 'Tìm kiếm giao dịch',
  keywordPlaceholder = 'Mã đơn, khách hàng, SKU, sản phẩm',
  loading = false,
  onApply,
}) => {
  const history = useHistory();
  const location = useLocation();
  const query = useMemo(() => getSalesReportQuery(location.search), [location.search]);
  const [values, setValues] = useState(query);
  const [expanded, setExpanded] = useState(true);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    setValues(query);
    if (query.salesperson || query.created_user) setAdvancedExpanded(true);
  }, [query]);

  const activePreset = useMemo(() => findActivePreset(values), [values]);
  const appliedFilters = useMemo(() => {
    const filters = [
      {
        key: 'date_range',
        label: `${formatShortDate(query.date_from)}–${formatShortDate(query.date_to)}`,
      },
    ];
    if (query.keyword) filters.push({ key: 'keyword', label: `Tìm kiếm: ${query.keyword}` });
    if (query.salesperson) filters.push({ key: 'salesperson', label: `Nhân viên bán hàng: ${query.salesperson}` });
    if (query.created_user) filters.push({ key: 'created_user', label: `Người tạo đơn: ${query.created_user}` });
    return filters;
  }, [query]);

  const update = (name, value) => {
    setValidationError('');
    setValues((current) => ({ ...current, [name]: value }));
  };

  const apply = (nextValues = values) => {
    if (nextValues.date_from && nextValues.date_to && nextValues.date_from > nextValues.date_to) {
      setValidationError('Từ ngày không được lớn hơn Đến ngày.');
      return;
    }

    setValidationError('');
    const params = new URLSearchParams();
    Object.entries({ ...nextValues, page: 1 }).forEach(([key, value]) => {
      if (`${value ?? ''}`.trim()) params.set(key, value);
    });
    history.replace({ pathname: location.pathname, search: params.toString() });
    onApply?.({ ...nextValues, page: 1 });
  };

  const reset = () => {
    const resetValues = {
      ...getPresetDates('30-days'),
      keyword: '',
      salesperson: '',
      created_user: '',
      page: 1,
      items_per_page: 20,
    };
    setValues(resetValues);
    setAdvancedExpanded(false);
    apply(resetValues);
  };

  const selectPreset = (preset) => {
    const nextValues = { ...values, ...getPresetDates(preset) };
    setValues(nextValues);
    apply(nextValues);
  };

  const removeAppliedFilter = (key) => {
    if (key === 'date_range') {
      const range = getPresetDates('30-days');
      apply({ ...query, ...range });
      return;
    }
    apply({ ...query, [key]: '' });
  };

  return (
    <section className={`bc_rr_filter_panel ${expanded ? 'is-expanded' : 'is-collapsed'}`}>
      <div className='bc_rr_filter_header'>
        <button
          type='button'
          className='bc_rr_filter_toggle'
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}>
          <span className='bc_rr_filter_title'>
            <FiFilter aria-hidden='true' />
            Bộ lọc báo cáo
            <span className='bc_rr_filter_badge' aria-label={`${appliedFilters.length} bộ lọc đang áp dụng`}>
              {appliedFilters.length}
            </span>
          </span>
          {expanded ? <FiChevronUp aria-hidden='true' /> : <FiChevronDown aria-hidden='true' />}
        </button>
        <button type='button' className='bc_rr_filter_clear' onClick={reset} disabled={loading}>
          Xóa bộ lọc
        </button>
      </div>

      {expanded ? (
        <div className='bc_rr_filter_content'>
          <div className='bc_rr_quick_dates' aria-label='Chọn nhanh khoảng thời gian'>
            {PRESETS.map(([value, label]) => (
              <button
                key={value}
                type='button'
                className={activePreset === value ? 'is-active' : ''}
                aria-pressed={activePreset === value}
                onClick={() => selectPreset(value)}>
                {label}
              </button>
            ))}
            {activePreset === 'custom' ? <span className='bc_rr_custom_preset'>Tùy chọn</span> : null}
          </div>

          <div className='bc_rr_filter_fields'>
            <label>
              <span>Từ ngày</span>
              <input
                type='date'
                value={values.date_from}
                aria-invalid={Boolean(validationError)}
                aria-describedby={validationError ? 'bc_rr_date_error' : undefined}
                onChange={(event) => update('date_from', event.target.value)}
              />
            </label>
            <label>
              <span>Đến ngày</span>
              <input
                type='date'
                value={values.date_to}
                min={values.date_from}
                aria-invalid={Boolean(validationError)}
                aria-describedby={validationError ? 'bc_rr_date_error' : undefined}
                onChange={(event) => update('date_to', event.target.value)}
              />
            </label>
            {includeDetailFilters || includeKeywordFilter ? (
              <label className='is-search'>
                <span>{keywordLabel}</span>
                <div className='bc_rr_input_icon'>
                  <FiSearch aria-hidden='true' />
                  <input
                    value={values.keyword}
                    placeholder={keywordPlaceholder}
                    onChange={(event) => update('keyword', event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') apply();
                    }}
                  />
                </div>
              </label>
            ) : <div className='bc_rr_filter_spacer' aria-hidden='true' />}
            <div className='bc_rr_filter_actions'>
              <button type='button' className='is-secondary' onClick={reset} disabled={loading}>
                <FiRefreshCw aria-hidden='true' /> Đặt lại
              </button>
              <button type='button' className='is-primary' disabled={loading} onClick={() => apply()}>
                <FiFilter aria-hidden='true' /> {loading ? 'Đang áp dụng' : 'Áp dụng'}
              </button>
            </div>
          </div>

          {validationError ? <p id='bc_rr_date_error' className='bc_rr_field_error' role='alert'>{validationError}</p> : null}

          {includeDetailFilters ? (
            <div className='bc_rr_advanced'>
              <button
                type='button'
                className='bc_rr_advanced_toggle'
                onClick={() => setAdvancedExpanded((value) => !value)}
                aria-expanded={advancedExpanded}>
                <FiSliders aria-hidden='true' />
                Bộ lọc nâng cao
                {advancedExpanded ? <FiChevronUp aria-hidden='true' /> : <FiChevronDown aria-hidden='true' />}
              </button>
              {advancedExpanded ? (
                <div className='bc_rr_advanced_fields'>
                  <label>
                    <span>Nhân viên bán hàng</span>
                    <input
                      value={values.salesperson}
                      placeholder='Mã nhân viên bán hàng'
                      onChange={(event) => update('salesperson', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Người tạo đơn</span>
                    <input
                      value={values.created_user}
                      placeholder='Tài khoản tạo đơn'
                      onChange={(event) => update('created_user', event.target.value)}
                    />
                  </label>
                  <p>Tìm theo bất kỳ nhân viên bán hàng được gán trên đơn. Thu ngân được hiển thị độc lập trong bảng; hiện chưa hỗ trợ lọc theo thu ngân.</p>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className='bc_rr_active_filters' aria-label='Bộ lọc đang áp dụng'>
            <span>Đang áp dụng:</span>
            {appliedFilters.map((filter) => (
              <button
                key={filter.key}
                type='button'
                title={`Xóa ${filter.label}`}
                onClick={() => removeAppliedFilter(filter.key)}>
                {filter.label}
                <FiX aria-hidden='true' />
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SalesReportFilters;
